import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import {outputKeyFromOutputTypeAndOutputId, parseOutputTypeAndOutputIdFromOutputKey, wrapInterfaceId} from "#template/interface-descriptors"

export function wrapComponentOutputsIds<T extends {[key: string]: unknown}>(
    scope: GraphBuilderScope,
    componentOutputs: BuilderInlet<T>,
    componentInstanceId: string,
    uniqueId: string,
): BuilderOutlet<T> {
    return scope.pureLambda(
        scope.tuple(componentOutputs, componentInstanceId),
        ([componentOutputs, componentInstanceId]) => {
            const wrappedComponentOutputs: Record<string, unknown> = {}
            for (const [outputKey, outputValue] of Object.entries(componentOutputs)) {
                const {outputType, outputId} = parseOutputTypeAndOutputIdFromOutputKey(outputKey)
                const parentOutputKey = outputKeyFromOutputTypeAndOutputId(outputType, wrapInterfaceId(componentInstanceId, outputId))
                wrappedComponentOutputs[parentOutputKey] = outputValue
            }
            return wrappedComponentOutputs as T
        },
        `wrapComponentOutputsIds-${uniqueId}`,
    )
}

export function mergeComponentOutputs<T extends {[key: string]: unknown}>(
    scope: GraphBuilderScope,
    outputsList: BuilderInlet<T>[],
    uniqueId: string,
): BuilderOutlet<T> {
    return scope.pureLambda(
        scope.sparseMerge(outputsList),
        (outputsList) => {
            let mergedOutputs: Record<string, unknown> = {}
            for (const outputs of outputsList) {
                if (outputs === null) continue
                mergedOutputs = {...mergedOutputs, ...outputs}
            }
            return mergedOutputs as T
        },
        `mergeComponentOutputs-${uniqueId}`,
    )
}
